import { FC, useState } from "react";
import RequestModal from "@/components/GetCardModals/RequestModal";
import { RequestTypes } from "@/api/interfaces/AdvantageCardRequest";
import { Grid, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";
import { Image, Icon } from "capitalrx-components-2.0/lib/general";
import EmailImage from "@/static/images/image-computer-min.png";

import styles from "./GetAdvantageCardCTA.module.scss";
import { getPrintCardImage, getTextCardImage } from "@/utils/branding";

const GetAdvantageCard: FC = (): JSX.Element => {
  const [showModalState, setShowModalState] = useState(false);
  const [modalType, setModalType] = useState<RequestTypes>(RequestTypes.null);
  const { columns4412 } = useResponsive({
    columns4412: {
      desktop: 4,
      tablet: 4,
      mobile: 12,
    },
  });

  const showModal = (type: RequestTypes): void => {
    setModalType(type);
    setShowModalState(true);
  };

  const closeModal = (): void => {
    setShowModalState(false);
    return null;
  };

  const textImage = getTextCardImage();
  const printImage = getPrintCardImage();

  return (
    <div className={styles.AdvantageCTA} data-testid="advantage-get-card-cta">
      <Paragraph textAlign="center" className="sectionTitle">
        Are you tired of overpaying for your medications?
        <span className="DisplayBlock">
          Download your <span className={styles.AdvantageCTABlue}>FREE</span> savings card to unlock discounts*
        </span>
      </Paragraph>

      <Grid className={styles.AdvantageCTAButtonWrapper}>
        <Grid.Spread columns={columns4412}>
          <button
            data-testid="text-button"
            className={`${styles.AdvantageCTAButton} ${styles.purple}`}
            onClick={() => showModal(RequestTypes.text)}
          >
            <div className={styles.AdvantageCTAButtonImage}>
              <Image src={textImage} alt="Request Card by Text" aria-hidden="true" />
            </div>
            <div className={styles.AdvantageCTAButtonText}>
              Text card
              <span>
                {" "}
                <Icon name="message-dots" color="white" className="caprx-icon-messages-bubble" aria-hidden="true" />
              </span>
            </div>
          </button>
        </Grid.Spread>
        <Grid.Spread columns={columns4412}>
          <button
            data-testid="email-button"
            className={`${styles.AdvantageCTAButton} ${styles.blue}`}
            onClick={() => showModal(RequestTypes.email)}
          >
            <div className={styles.AdvantageCTAButtonImage}>
              <Image src={EmailImage} alt="Request Card by Email" aria-hidden="true" />
            </div>
            <div className={styles.AdvantageCTAButtonText}>
              Email card
              <span>
                {" "}
                <Icon name="envelope" color="white" className="caprx-icon-mail-letter" aria-hidden="true" />
              </span>
            </div>
          </button>
        </Grid.Spread>
        <Grid.Spread columns={columns4412}>
          <button
            data-testid="print-button"
            className={`${styles.AdvantageCTAButton} ${styles.green}`}
            onClick={() => showModal(RequestTypes.print)}
          >
            <div className={styles.AdvantageCTAButtonImage}>
              <Image src={printImage} alt="Print Card" aria-hidden="true" />
            </div>
            <div className={styles.AdvantageCTAButtonText}>
              Print card
              <span>
                {" "}
                <Icon name="print" color="white" className="caprx-icon-print" aria-hidden="true" />
              </span>
            </div>
          </button>
        </Grid.Spread>
      </Grid>

      <Paragraph textAlign="center" className={styles.AdvantageCTADisclaimer} testId="get-advantage-bulletpoints">
        No membership fees <span>&bull;</span> No credit card required <span>&bull;</span> No expiration <span>&bull;</span> One card for
        the whole family
      </Paragraph>

      <Paragraph textAlign="center" className={styles.AdvantageCTANote} testId="get-advantage-disclaimer">
        *This savings program is not health insurance and cannot be used in conjunction with insurance.
        The range of discounts for prescription services provided under the card will vary depending on the type of pharmacy and medication
        received. Plan member is obligated to pay for all prescriptions, but will receive a discount from those pharmacies that have
        contracted with the discount card.
      </Paragraph>

      <RequestModal type={modalType} visible={showModalState} onClose={closeModal} />
    </div>
  );
};

export default GetAdvantageCard;
